import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, ShoppingCart } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AverageOrderValueCalculator = () => {
  const [totalRevenue, setTotalRevenue] = useState(100000);
  const [numberOfOrders, setNumberOfOrders] = useState(1000);
  const [averageOrderValue, setAverageOrderValue] = useState(0);

  useEffect(() => {
    calculateAverageOrderValue();
  }, [totalRevenue, numberOfOrders]);

  const calculateAverageOrderValue = () => {
    const aov = numberOfOrders > 0 ? totalRevenue / numberOfOrders : 0;
    setAverageOrderValue(parseFloat(aov.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/average-order-value-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Average Order Value (AOV) Calculator: Boost Transaction Value | Free Tool</title>
        <meta name="description" content="Calculate your Average Order Value instantly. Learn how to increase transaction values, compare with industry benchmarks, and get expert tips to boost your AOV." />
        <meta name="keywords" content="average order value calculator, AOV calculator, transaction value calculator, sales metrics calculator, e-commerce calculator" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free AOV Calculator: Maximize Your Transaction Values" />
        <meta property="og:description" content="Calculate your Average Order Value instantly. Get actionable insights to increase transaction values and boost revenue." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free AOV Calculator: Maximize Your Transaction Values" />
        <meta property="twitter:description" content="Calculate your Average Order Value instantly. Get actionable insights to increase transaction values and boost revenue." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Average Order Value Calculator",
            "description": "Free calculator to measure and optimize average order value (AOV)",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Average Order Value",
              "Compare with Industry Benchmarks",
              "Get Optimization Strategies",
              "Track Revenue Impact",
              "Monitor Sales Performance"
            ],
            "about": {
              "@type": "Thing",
              "name": "Average Order Value",
              "description": "The average amount spent each time a customer places an order on a website or in a store."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "E-commerce Managers, Retail Owners, Marketing Professionals"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Average Order Value",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter your total revenue"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input the number of orders"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your calculated AOV and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Average Order Value Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalRevenue}
                  onChange={(e) => setTotalRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Number of Orders</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <ShoppingCart className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={numberOfOrders}
                  onChange={(e) => setNumberOfOrders(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${averageOrderValue}
            <ShoppingCart className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Average Order Value</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Average Order Value (AOV) is ${averageOrderValue}. This means that, on average, each customer spends ${averageOrderValue} per order. A higher AOV generally indicates that you're effectively maximizing the value of each transaction.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Fashion E-commerce: $100-150</li>
          <li>Electronics: $250-300</li>
          <li>Home & Garden: $150-200</li>
          <li>Beauty & Cosmetics: $50-75</li>
          <li>Luxury Retail: $400+</li>
          <li>Food & Beverage: $30-50</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Increase Average Order Value</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Implement upselling and cross-selling strategies</li>
          <li>Offer product bundles or packages</li>
          <li>Provide volume discounts to encourage larger purchases</li>
          <li>Set a free shipping threshold above your current AOV</li>
          <li>Use personalized product recommendations</li>
          <li>Implement a loyalty program that rewards higher spending</li>
          <li>Create product bundles based on complementary items</li>
          <li>Offer limited-time deals on bulk purchases</li>
          <li>Use dynamic pricing strategies</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact on Business Growth</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>10% increase in AOV can lead to 10% revenue growth without additional traffic</li>
          <li>Higher AOV typically results in better profit margins</li>
          <li>Increased AOV can offset customer acquisition costs</li>
          <li>Better ROI on marketing spend with higher transaction values</li>
          <li>More efficient inventory management with larger orders</li>
        </ul>
      </div>
    </div>
  );
};

export default AverageOrderValueCalculator;
